<template>
  <nav class="docs-anchor-nav" aria-label="On this page">
    <ul class="docs-anchor-nav__list">
      <li
        v-for="item in items"
        :key="item.anchor"
        class="docs-anchor-nav__item"
      >
        <a
          :href="'#' + item.anchor"
          class="docs-anchor-nav__link"
          :class="{ 'is-active': activeAnchor === item.anchor }"
          :aria-current="activeAnchor === item.anchor ? 'true' : undefined"
          :aria-label="`Jump to ${item.label}`"
          @click.prevent="onAnchorClick(item.anchor)"
        >
          {{ item.label }}
        </a>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { useScrollToAnchor } from '@/composables/useScrollToAnchor';

interface AnchorItem {
  label: string;
  anchor: string;
}

interface Props {
  items: AnchorItem[];
  activeAnchor?: string;
}

withDefaults(defineProps<Props>(), {
  activeAnchor: ''
});

const { scrollToHash } = useScrollToAnchor();

const onAnchorClick = (anchor: string): void => {
  scrollToHash(anchor);
};
</script>

<style scoped lang="scss">
.docs-anchor-nav {
  display: none;
  width: 200px;
  flex-shrink: 0;
  position: sticky;
  top: calc(var(--nav-height, 56px) + var(--space-6));
  align-self: flex-start;
  border-left: 2px solid var(--mist);

  @media (min-width: 960px) {
    display: block;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  &__link {
    display: block;
    padding-left: var(--space-4);
    font-size: var(--fs-body-sm);
    color: var(--stone);
    text-decoration: none;
    transition:
      color var(--d-fast) var(--ease-out),
      font-weight var(--d-fast) var(--ease-out);

    &:hover {
      color: var(--ink);
    }

    &.is-active {
      color: var(--ink);
      font-weight: var(--fw-medium);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
      border-radius: var(--r-2);
    }
  }
}
</style>
